"use client"

import { ExternalLink, Loader2, MousePointerClick } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useAddStorefrontWidgetMutation } from "@/hooks/mutations/shopify-storefront-widget"
import type { StorefrontWidgetStoreView } from "@/server-actions/shopify/storefront-widget"

interface ShopifyAddChatDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  channelId: string
  store: StorefrontWidgetStoreView
}

/** Confirm, add, then send the merchant to the theme editor; nothing reaches the store before "Add chat". */
export function ShopifyAddChatDialog({ open, onOpenChange, channelId, store }: ShopifyAddChatDialogProps) {
  const add = useAddStorefrontWidgetMutation()
  const added = add.isSuccess

  return (
    <Dialog open={open} onOpenChange={next => { if (!add.isPending) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md">
        {/* Keyed so a translated page shows the new step instead of the previous translated text. */}
        <DialogHeader key={added ? "added" : "confirm"}>
          <DialogTitle className="break-words">
            {added ? `Chat added to ${store.publicDomain}` : `Add the chat to ${store.publicDomain}`}
          </DialogTitle>
          <DialogDescription>
            {added
              ? "Switch it on in the theme editor."
              : "If the chat is already on in your theme, it shows on your store right away. Otherwise you switch it on in the theme editor next."}
          </DialogDescription>
        </DialogHeader>
        {added && (
          <Alert role="note" className="border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-300">
            <MousePointerClick className="h-4 w-4" />
            <AlertDescription>
              <span>Click </span><strong>Save</strong><span> at the top right in Shopify to show it on your store.</span>
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter className="gap-2">
          {added ? (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Done
              </Button>
              <Button asChild variant="brand">
                <a href={store.themeEditorUrl} target="_blank" rel="noopener noreferrer" onClick={() => onOpenChange(false)}>
                  <span>Open theme editor</span><ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={add.isPending} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="brand"
                disabled={add.isPending}
                onClick={() => add.mutate({ channelId, integrationId: store.integrationId })}
              >
                {add.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>Add chat</span>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
